import * as React from "react";
import * as ReactDOM from "react-dom/client";
import App from "./App";
import * as serviceWorker from "../registerServiceWorker";
import configureStore from "./store/configureStore";
import { initialState } from "./store/initialState";
import { StoreState } from "./store/storeState";
import { Store } from "redux";
import { HashRouter } from "react-router-dom";
import { Provider } from "react-redux";
import "../polyfills";
import { setupAxiosInterceptors } from "../common/config/axiosConfig";
import { testJwtTokenService, setTestToken } from "../common/services/jwtTokenService.test";

/**
 * REACT 18 MIGRATION: Suppress deprecated lifecycle warnings from outdated third-party libraries
 *
 * These warnings are caused by dependencies that haven't been updated for React 18:
 * - @material-ui/core v3.9.4 (2018) - Should upgrade to @mui/material v5+
 * - material-ui-pickers v2.2.4 (2019) - Should upgrade to @mui/x-date-pickers
 * - react-router v4.3.1 (2018) - Should upgrade to v6+
 * - google-maps-react v2.0.6 - May need alternative or update
 * - react-star-rating-component v1.4.1 - Very old, consider replacement
 *
 * TODO: Track and upgrade these packages to remove this suppression
 * @see https://react.dev/blog/2022/03/08/react-18-upgrade-guide#updates-to-strict-mode
 */
if (process.env.NODE_ENV === 'development') {
  const originalWarn = console.warn;
  const suppressedWarnings = new Set<string>();

  console.warn = (...args: any[]) => {
    const msg = args[0];
    if (typeof msg === 'string') {
      // Suppress known React 16 lifecycle warnings from third-party libraries
      const isDeprecatedWarning =
        msg.includes('React.createFactory()') ||
        msg.includes('componentWillMount') ||
        msg.includes('componentWillReceiveProps') ||
        msg.includes('componentWillUpdate') ||
        msg.includes('UNSAFE_componentWillMount') ||
        msg.includes('UNSAFE_componentWillReceiveProps');

      if (isDeprecatedWarning) {
        // Track unique warnings for debugging (comment out if too noisy)
        if (!suppressedWarnings.has(msg)) {
          suppressedWarnings.add(msg);
          console.info('[SUPPRESSED REACT WARNING]:', msg.substring(0, 150) + '...');
        }
        return; // Suppress the warning
      }
    }
    originalWarn.apply(console, args);
  };
}

// Setup axios interceptors for JWT token handling
setupAxiosInterceptors();

const store: Store<StoreState> = configureStore(initialState);

// Removed sessionStorage restore to prevent PII storage on client
// Page refresh now requires re-authentication

const renderApp = () => {
  return (
    // The Provider component is a special React-Redux component that provides the hooks to the
    // store.
    <Provider store={store}>
      <HashRouter {...({} as any)}>
        <App />
      </HashRouter>
    </Provider>
  );
};

const rootElement = document.getElementById("root") as HTMLElement;
const root = ReactDOM.createRoot(rootElement);

function registerHMR() {
  type ModuleHMR = typeof module & {
    hot?: {
      accept(dependencies: string | string[], callback: (updatedDependencies: any[]) => void): void;
    };
  };
  if ((module as ModuleHMR).hot) {
    (module as ModuleHMR).hot!.accept("./App", () => {
      root.render(
        <Provider store={store}>
          <HashRouter {...({} as any)}>
            <App />
          </HashRouter>
        </Provider>
      );
    });
  }
}

root.render(renderApp());

serviceWorker.unregisterServiceWorker();
registerHMR();

// Export debugging functions for browser console testing
if (process.env.NODE_ENV === 'development') {
  (window as any).testJwtToken = testJwtTokenService;
  (window as any).setTestToken = setTestToken;
}

export { store };
